import { lazy, type ComponentType } from 'react';

// Nach einem Deploy ändern sich die gehashten Dateinamen der Seiten-Chunks;
// alte werden vom Server nicht mehr ausgeliefert. Wer die App währenddessen
// offen hat, lädt beim nächsten Seitenwechsel eine Datei an, die es nicht mehr
// gibt — der import() schlägt fehl und die Seite bleibt ohne Fehlermeldung auf
// „Lade…" stehen, bis man von Hand neu lädt. Diese Wrapper-Funktion fängt
// genau diesen Fall ab und lädt die Seite einmalig automatisch neu (holt damit
// eine frische index.html mit den aktuellen Chunk-Namen).
//
// Der sessionStorage-Zähler verhindert eine Reload-Schleife, falls der Import
// aus einem anderen Grund (z. B. echter Bug im Chunk) dauerhaft fehlschlägt.
//
// Wichtig: Die Sperre ist ZEITLICH begrenzt und wird nach einem erfolgreichen
// Laden wieder gelöscht. Vorher blieb sie die ganze Sitzung stehen — nach dem
// ersten geglückten Selbstheilungs-Reload war der Schutz für diese Seite
// verbraucht. Ein zweiter Deploy in derselben Sitzung (bei uns über den Tag
// völlig normal) endete dann in einem Fehler ins Leere: kein Reload mehr, kein
// Inhalt, keine Meldung. Genau das Bild „ich bin länger drin, wechsle auf
// Kunden, nichts lädt — Browser-Refresh hilft".
const RELOAD_SPERRE_MS = 60_000;

function sperrKey(chunkName: string): string {
  return `cc-chunk-reload:${chunkName}`;
}

/** Darf für diesen Chunk (wieder) automatisch neu geladen werden? */
function darfNeuLaden(chunkName: string): boolean {
  try {
    const roh = sessionStorage.getItem(sperrKey(chunkName));
    if (!roh) return true;
    const zuletzt = Number(roh);
    // Alte Einträge aus der Vorversion ('1') zählen als „gerade eben".
    if (!Number.isFinite(zuletzt) || zuletzt === 1) return false;
    return Date.now() - zuletzt > RELOAD_SPERRE_MS;
  } catch {
    return false; // kein sessionStorage (Privatmodus) => lieber nicht schleifen
  }
}

function merkeReload(chunkName: string): void {
  try { sessionStorage.setItem(sperrKey(chunkName), String(Date.now())); } catch { /* egal */ }
}

function loescheSperre(chunkName: string): void {
  try { sessionStorage.removeItem(sperrKey(chunkName)); } catch { /* egal */ }
}

export function lazyRetry<T extends ComponentType<unknown>>(
  importer: () => Promise<{ default: T }>,
  chunkName: string,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      const mod = await importer();
      // Geklappt — die Sperre darf weg, damit ein späterer Deploy in derselben
      // Sitzung wieder automatisch abgefangen wird.
      loescheSperre(chunkName);
      return mod;
    } catch (err) {
      if (darfNeuLaden(chunkName)) {
        merkeReload(chunkName);
        window.location.reload();
        // Reload läuft asynchron — bis dahin nie auflösen, sonst rendert
        // React kurz den (fehlgeschlagenen) Chunk.
        return new Promise<never>(() => {});
      }
      // Zweiter Fehlschlag kurz hintereinander: nicht noch einmal neu laden,
      // sondern weiterreichen. Die Fehlergrenze in App.tsx zeigt daraus eine
      // verständliche Meldung mit „Neu laden" — statt einer leeren Seite.
      throw err;
    }
  });
}
